// Space-aware coordinate transforms

import { CoordinateSpace, Coordinates, ContentRect, CoordinateSpaceUtils } from './CoordinateSpaces';

export interface TransformContext {
  contentRect?: ContentRect;
  pageIndex?: number;
  pageHeight?: number;
  pageGap?: number;
  devicePixelRatio?: number;
  zoomLevel?: number;
}

export class SpaceTransforms {
  /**
   * Transform coordinates between explicit spaces
   * Refuses to run if source space is unknown
   */
  static transform(
    coords: Coordinates,
    targetSpace: CoordinateSpace,
    context: TransformContext = {}
  ): Coordinates {
    // Validate input coordinates
    CoordinateSpaceUtils.validateCoordinates(coords);
    
    if (!coords.space) {
      throw new Error('Source coordinate space must be explicit');
    }

    // Same space - return as-is
    if (coords.space === targetSpace) {
      return { ...coords };
    }

    let result: Coordinates;

    // Content → Page: Add content rectangle offset
    if (coords.space === CoordinateSpace.CONTENT && targetSpace === CoordinateSpace.PAGE) {
      if (!context.contentRect) {
        throw new Error('ContentRect required for Content→Page transform');
      }
      
      CoordinateSpaceUtils.validateContentRect(context.contentRect);
      
      result = {
        x: coords.x + context.contentRect.x,
        y: coords.y + context.contentRect.y,
        space: CoordinateSpace.PAGE
      };
    }
    // Page → Content: Subtract content rectangle offset
    else if (coords.space === CoordinateSpace.PAGE && targetSpace === CoordinateSpace.CONTENT) {
      if (!context.contentRect) {
        throw new Error('ContentRect required for Page→Content transform');
      }
      
      CoordinateSpaceUtils.validateContentRect(context.contentRect);
      
      result = {
        x: coords.x - context.contentRect.x,
        y: coords.y - context.contentRect.y,
        space: CoordinateSpace.CONTENT
      };
    }
    // Page → Viewport: Add page stacking offset
    else if (coords.space === CoordinateSpace.PAGE && targetSpace === CoordinateSpace.VIEWPORT) {
      const pageOffset = context.pageIndex ? 
        context.pageIndex * ((context.pageHeight || 0) + (context.pageGap || 0)) : 0;
      
      result = {
        x: coords.x,
        y: coords.y + pageOffset,
        space: CoordinateSpace.VIEWPORT
      };
    }
    // Viewport → Page: Subtract page stacking offset
    else if (coords.space === CoordinateSpace.VIEWPORT && targetSpace === CoordinateSpace.PAGE) {
      const pageOffset = context.pageIndex ? 
        context.pageIndex * ((context.pageHeight || 0) + (context.pageGap || 0)) : 0;
      
      result = {
        x: coords.x,
        y: coords.y - pageOffset,
        space: CoordinateSpace.PAGE
      };
    }
    // Viewport → Device: Apply device pixel ratio and zoom
    else if (coords.space === CoordinateSpace.VIEWPORT && targetSpace === CoordinateSpace.DEVICE) {
      const dpr = context.devicePixelRatio || 1;
      const zoom = context.zoomLevel || 1;
      
      result = {
        x: coords.x * dpr * zoom,
        y: coords.y * dpr * zoom,
        space: CoordinateSpace.DEVICE
      };
    }
    // Device → Viewport: Reverse device pixel ratio and zoom
    else if (coords.space === CoordinateSpace.DEVICE && targetSpace === CoordinateSpace.VIEWPORT) {
      const dpr = context.devicePixelRatio || 1;
      const zoom = context.zoomLevel || 1;
      
      result = {
        x: coords.x / (dpr * zoom),
        y: coords.y / (dpr * zoom),
        space: CoordinateSpace.VIEWPORT
      };
    }
    // HarfBuzz → Canvas: Apply 0.6x scaling factor
    else if (coords.space === CoordinateSpace.HARFBUZZ && targetSpace === CoordinateSpace.PAGE) {
      result = {
        x: coords.x * 0.6,
        y: coords.y * 0.6,
        space: CoordinateSpace.PAGE
      };
    }
    // HarfBuzz → Content: Scale and subtract content offset
    else if (coords.space === CoordinateSpace.HARFBUZZ && targetSpace === CoordinateSpace.CONTENT) {
      if (!context.contentRect) {
        throw new Error('ContentRect required for HarfBuzz→Content transform');
      }
      
      result = {
        x: coords.x * 0.6 - context.contentRect.x,
        y: coords.y * 0.6 - context.contentRect.y,
        space: CoordinateSpace.CONTENT
      };
    }
    // HarfBuzz → Viewport: Scale and add page offset
    else if (coords.space === CoordinateSpace.HARFBUZZ && targetSpace === CoordinateSpace.VIEWPORT) {
      const pageOffset = context.pageIndex ? 
        context.pageIndex * ((context.pageHeight || 0) + (context.pageGap || 0)) : 0;
      
      result = {
        x: coords.x * 0.6,
        y: coords.y * 0.6 + pageOffset,
        space: CoordinateSpace.VIEWPORT
      };
    }
    else {
      throw new Error(`Transform from ${coords.space} to ${targetSpace} not implemented`);
    }

    // Log transform for debugging
    CoordinateSpaceUtils.logTransform(coords, result, `${coords.space}→${targetSpace}`, context);
    
    return result;
  }

  /**
   * Batch transform multiple coordinates
   */
  static transformBatch(
    coordsList: Coordinates[],
    targetSpace: CoordinateSpace,
    context: TransformContext = {}
  ): Coordinates[] {
    return coordsList.map(coords => this.transform(coords, targetSpace, context));
  }

  /**
   * Chain multiple transforms in sequence
   */
  static transformChain(
    coords: Coordinates,
    transforms: Array<{
      targetSpace: CoordinateSpace;
      context?: TransformContext;
    }>
  ): Coordinates {
    let result = coords;
    
    for (const transform of transforms) {
      result = this.transform(result, transform.targetSpace, transform.context || {});
    }
    
    return result;
  }

  /**
   * Get the reverse transform for a given transform
   */
  static getReverseTransform(
    fromSpace: CoordinateSpace,
    toSpace: CoordinateSpace
  ): { fromSpace: CoordinateSpace; toSpace: CoordinateSpace } {
    return { fromSpace: toSpace, toSpace: fromSpace };
  }

  /**
   * Check if a transform is supported
   */
  static isTransformSupported(fromSpace: CoordinateSpace, toSpace: CoordinateSpace): boolean {
    if (fromSpace === toSpace) return true;
    
    const supportedTransforms = [
      [CoordinateSpace.CONTENT, CoordinateSpace.PAGE],
      [CoordinateSpace.PAGE, CoordinateSpace.CONTENT],
      [CoordinateSpace.PAGE, CoordinateSpace.VIEWPORT],
      [CoordinateSpace.VIEWPORT, CoordinateSpace.PAGE],
      [CoordinateSpace.VIEWPORT, CoordinateSpace.DEVICE],
      [CoordinateSpace.DEVICE, CoordinateSpace.VIEWPORT],
      [CoordinateSpace.HARFBUZZ, CoordinateSpace.PAGE],
      [CoordinateSpace.HARFBUZZ, CoordinateSpace.CONTENT],
      [CoordinateSpace.HARFBUZZ, CoordinateSpace.VIEWPORT]
    ];
    
    return supportedTransforms.some(([from, to]) => from === fromSpace && to === toSpace);
  }

  /**
   * Calculate bounding box in target coordinate space
   */
  static transformBoundingBox(
    bbox: { x: number; y: number; width: number; height: number },
    fromSpace: CoordinateSpace,
    targetSpace: CoordinateSpace,
    context: TransformContext = {}
  ): { x: number; y: number; width: number; height: number } {
    // Transform top-left corner
    const topLeft = this.transform(
      { x: bbox.x, y: bbox.y, space: fromSpace },
      targetSpace,
      context
    );
    
    // Transform bottom-right corner
    const bottomRight = this.transform(
      { x: bbox.x + bbox.width, y: bbox.y + bbox.height, space: fromSpace },
      targetSpace,
      context
    );
    
    return {
      x: Math.min(topLeft.x, bottomRight.x),
      y: Math.min(topLeft.y, bottomRight.y),
      width: Math.abs(bottomRight.x - topLeft.x),
      height: Math.abs(bottomRight.y - topLeft.y)
    };
  }
}
